import * as React from 'react'
import { observer } from 'mobx-react'
import Avatar from 'components/Avatar'
import {
  CustomSelect,
  SingleItemSelectedDefault,
  useCustomSelectStateFromInfiniteScrollProvider,
} from 'components/inputs/CustomSelect'
import SearchResultItem from 'components/inputs/CustomSelect/SearchResultItem'
import { FormGroup } from 'components/inputs'
import RequestProviderSelectorViewModel from 'jobs/creation/RequestProviderSelectorViewModel'
import { RequestedProviderSelection } from 'jobs/creation/DraftChange'

interface RequestProviderSelectorProps {
  vm: RequestProviderSelectorViewModel
}

/**
 * Request Provider Selector
 */
export default observer(function RequestProviderSelector({
  vm,
}: RequestProviderSelectorProps) {
  const selectProps = useCustomSelectStateFromInfiniteScrollProvider({
    scrollProvider: vm.scrollProvider,
    searchText: vm.searchText.value,
    onSearchTextChange: vm.triggerSearch,
    searchResults: vm.filteredMembers,
  })

  const nullSelectionPlaceholder = vm.nullSelectionPlaceholder

  return (
    <FormGroup>
      <CustomSelect
        {...selectProps}
        placeholder="Select provider"
        itemToString={(v) =>
          v === RequestedProviderSelection.firstAvailable
            ? nullSelectionPlaceholder
            : String(v?.displayName)
        }
        disabled={vm.disabled}
        focus$={vm.step.becomeActive$}
        onFocus={vm.step.onBecameActive}
        value={vm._requestedProvider ?? undefined}
        onChange={vm.selectProvider}
        multi={false}
        data-test="compact-request-provider-selector"
        renderItem={(itemProps) => {
          const display = getDisplayPropsForProvider(
            itemProps.item,
            nullSelectionPlaceholder
          )

          return (
            <SingleItemSelectedDefault
              visual={
                display.avatar && (
                  <Avatar
                    size={24}
                    subject={{
                      displayName: display.name,
                      avatar: display.avatar,
                    }}
                  />
                )
              }
              {...itemProps}
            />
          )
        }}
        renderSearchItem={({ item, itemToString, ...itemProps }) => {
          const display = getDisplayPropsForProvider(
            item,
            nullSelectionPlaceholder
          )

          return (
            <SearchResultItem
              {...itemProps}
              visual={
                display.avatar && (
                  <Avatar
                    size={24}
                    subject={{
                      avatar: display.avatar,
                      displayName: display.name,
                    }}
                  />
                )
              }
            >
              {display.name}
            </SearchResultItem>
          )
        }}
      />
    </FormGroup>
  )
})

/**
 * Gets display props for a provider.
 */
function getDisplayPropsForProvider(
  provider: RequestedProviderSelection,
  nullSelectionPlaceholder: string
) {
  if (provider === RequestedProviderSelection.firstAvailable) {
    return {
      name: nullSelectionPlaceholder,
      avatar: null,
    }
  }

  return {
    name: provider.displayName,
    avatar: provider.avatar,
  }
}
